import { Component } from 'react';
import { createRecordingDialogEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import ColorSchemeRegistry from '../../../base/color-scheme/ColorSchemeRegistry';
import { _abstractMapStateToProps } from '../../../base/dialog/functions';
import { MEET_FEATURES } from '../../../base/jwt/constants';
import { isJwtFeatureEnabled } from '../../../base/jwt/functions';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { authorizeDropbox, updateDropboxToken } from '../../../dropbox/actions';
import { isE2EEOnInMeeting } from '../../../e2ee/functions';
import { setFollowMeRecorderExclusive } from '../../../follow-me/actions';
import { isFollowMeActive, isFollowMeRecorderActive } from '../../../follow-me/functions';
import { isVpaasMeeting } from '../../../jaas/functions';
import { getAvailableSubtitlesLanguages } from '../../../subtitles/functions.any';
import { canAddTranscriber, isRecorderTranscriptionsRunning } from '../../../transcribing/functions';
import { RECORDING_TYPES } from '../../constants';
import { hasRecordingOrTranscriptionFeature, isLiveStreamingRunning, supportsLocalRecording } from '../../functions';
/**
 * React Component for the recording & transcription dialog content: two
 * sections (audio & video recording, transcription), each with its own
 * start/stop button and a collapsible options area, plus footer buttons
 * acting on both services at once.
 *
 * @augments Component
 */
class AbstractStartRecordingDialogContent extends Component {
    /**
     * Initializes a new {@code AbstractStartRecordingDialogContent} instance.
     *
     * @inheritdoc
     */
    constructor(props) {
        super(props);
        // Bind event handler; it bounds once for every instance.
        this._onSignIn = this._onSignIn.bind(this);
        this._onSignOut = this._onSignOut.bind(this);
        this._onRecordingServiceChange = this._onRecordingServiceChange.bind(this);
        this._onFollowMeRecorderChange = this._onFollowMeRecorderChange.bind(this);
        this._onToggleRecordingOptions = this._onToggleRecordingOptions.bind(this);
        this._onToggleTranscriptionOptions = this._onToggleTranscriptionOptions.bind(this);
        this._onToggleLanguageList = this._onToggleLanguageList.bind(this);
        // Both sections start collapsed, except transcription options are pre-expanded when a
        // caller (e.g. the transcription nudge) explicitly asks to focus transcription instead
        // of recording by passing recordAudioAndVideo: false.
        this.state = {
            showLanguageList: false,
            showRecordingOptions: false,
            showTranscriptionOptions: props.recordAudioAndVideo === false
        };
    }
    /**
     * Implements {@code Component#componentDidUpdate}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        // Auto sign-out when the user chooses another recording service.
        if (prevProps.selectedRecordingService === RECORDING_TYPES.DROPBOX
            && this.props.selectedRecordingService !== RECORDING_TYPES.DROPBOX && this.props.isTokenValid) {
            this._onSignOut();
        }
    }
    /**
     * Toggles the recording section options.
     *
     * @returns {void}
     */
    _onToggleRecordingOptions() {
        this.setState({ showRecordingOptions: !this.state.showRecordingOptions });
    }
    /**
     * Toggles the transcription section options.
     *
     * @returns {void}
     */
    _onToggleTranscriptionOptions() {
        this.setState({ showTranscriptionOptions: !this.state.showTranscriptionOptions });
    }
    /**
     * Toggles the inline language list.
     *
     * @returns {void}
     */
    _onToggleLanguageList() {
        this.setState({ showLanguageList: !this.state.showLanguageList });
    }
    /**
     * Returns the list of recording services (RECORDING_TYPES values) the
     * participant can currently pick from.
     *
     * Cloud based services (Jitsi recording service, Dropbox) require the
     * recording JWT feature and are unavailable while a live stream runs
     * (both use Jibri). Dropbox is additionally unavailable while a session
     * is running (integrationsEnabled covers that).
     *
     * @returns {Array<string>}
     */
    _getRecordingServiceOptions() {
        const { _isLiveStreamRunning, _localRecordingAvailable, _renderRecording, fileRecordingsServiceEnabled, integrationsEnabled } = this.props;
        const options = [];
        if (_renderRecording && !_isLiveStreamRunning) {
            if (fileRecordingsServiceEnabled) {
                options.push(RECORDING_TYPES.JITSI_REC_SERVICE);
            }
            if (integrationsEnabled) {
                options.push(RECORDING_TYPES.DROPBOX);
            }
        }
        if (_localRecordingAvailable) {
            options.push(RECORDING_TYPES.LOCAL);
        }
        return options;
    }
    /**
     * Whether the audio & video recording section should be rendered.
     *
     * @param {Array<string>} [options] - The result of {@code _getRecordingServiceOptions()},
     * when the caller already computed it (avoids recomputing it a second time).
     * @returns {boolean}
     */
    _shouldRenderRecordingSection(options = this._getRecordingServiceOptions()) {
        return this.props.recordingRunning || options.length > 0;
    }
    /**
     * Whether the transcription section should be rendered.
     *
     * @returns {boolean}
     */
    _shouldRenderTranscriptionSection() {
        return this._canStartTranscribing() || this.props.transcriptionRunning;
    }
    /**
     * Whether the footer buttons (start both / stop both) should be rendered.
     * They only make sense when the participant can act on both services.
     *
     * @returns {boolean}
     */
    _shouldRenderFooter() {
        return this._shouldRenderRecordingSection() && this._shouldRenderTranscriptionSection();
    }
    /**
     * Whether the file sharing content should be rendered or not.
     *
     * @returns {boolean}
     */
    _shouldRenderFileSharingContent() {
        const { fileRecordingsServiceEnabled, fileRecordingsServiceSharingEnabled, isVpaas, selectedRecordingService } = this.props;
        if (!fileRecordingsServiceEnabled
            || !fileRecordingsServiceSharingEnabled
            || isVpaas
            || selectedRecordingService !== RECORDING_TYPES.JITSI_REC_SERVICE) {
            return false;
        }
        return true;
    }
    /**
     * Whether the recorder follow me option should be rendered: moderators
     * only, and only for the cloud based services which spawn a recorder
     * participant that can follow (local recordings record the own view).
     *
     * @returns {boolean}
     */
    _shouldRenderFollowMeRecorder() {
        const { _isModerator, selectedRecordingService } = this.props;
        return _isModerator
            && (selectedRecordingService === RECORDING_TYPES.JITSI_REC_SERVICE
                || selectedRecordingService === RECORDING_TYPES.DROPBOX);
    }
    /**
     * Whether the save transcription content should be rendered or not.
     *
     * @returns {boolean}
     */
    _canStartTranscribing() {
        return this.props._canStartTranscribing;
    }
    /**
     * Returns the translation key labelling the given recording service in
     * the storage service picker.
     *
     * @param {string} service - One of the RECORDING_TYPES values.
     * @returns {string}
     */
    _getServiceLabelKey(service) {
        switch (service) {
            case RECORDING_TYPES.JITSI_REC_SERVICE:
                return this.props.isVpaas ? 'recording.serviceDescriptionCloud' : 'recording.serviceName';
            case RECORDING_TYPES.DROPBOX:
                return 'recording.dropbox';
            case RECORDING_TYPES.LOCAL:
                return 'recording.localRecording';
        }
        return '';
    }
    /**
     * Returns the one line summary of the recording options, shown in the
     * collapsed accordion header: the selected storage service followed by
     * the state of its suboptions.
     *
     * @returns {string}
     */
    _getRecordingSummary() {
        const { isTokenValid, localRecordingOnlySelf, selectedRecordingService, sharingSetting, t, userName } = this.props;
        if (!selectedRecordingService) {
            return '';
        }
        const parts = [t(this._getServiceLabelKey(selectedRecordingService))];
        switch (selectedRecordingService) {
            case RECORDING_TYPES.JITSI_REC_SERVICE:
                if (this._shouldRenderFileSharingContent()) {
                    parts.push(t(sharingSetting ? 'recording.linkShared' : 'recording.linkNotShared'));
                }
                break;
            case RECORDING_TYPES.DROPBOX:
                parts.push(isTokenValid ? t('recording.loggedIn', { userName }) : t('recording.notSignedIn'));
                break;
            case RECORDING_TYPES.LOCAL:
                if (localRecordingOnlySelf) {
                    parts.push(t('recording.onlyRecordSelf'));
                }
                break;
        }
        if (this._shouldRenderFollowMeRecorder() && this._isFollowMeRecorderChecked()) {
            parts.push(t('settings.followMeRecorder'));
        }
        return parts.join(' · ');
    }
    /**
     * Returns the one line summary of the transcription options, shown in the
     * collapsed accordion header: the selected language.
     *
     * @returns {string}
     */
    _getTranscriptionSummary() {
        const { selectedLanguage, t } = this.props;
        return selectedLanguage ? t(selectedLanguage) : '';
    }
    /**
     * Handler for recording service selection changes.
     *
     * @param {string} service - The newly selected service.
     * @returns {void}
     */
    _onRecordingServiceChange(service) {
        const { isTokenValid, onRecordingServiceChange, selectedRecordingService } = this.props;
        if (service === selectedRecordingService) {
            return;
        }
        onRecordingServiceChange(service);
        if (service === RECORDING_TYPES.DROPBOX && !isTokenValid) {
            this._onSignIn();
        }
    }
    /**
     * Handler for the recorder follow me setting.
     *
     * @param {boolean} enabled - The new value.
     * @returns {void}
     */
    _onFollowMeRecorderChange(enabled) {
        const value = enabled ?? !this.props._followMeRecorderEnabled;
        this.props.dispatch(setFollowMeRecorderExclusive(value));
    }
    /**
     * Whether the recorder follow me option is checked.
     *
     * @returns {boolean}
     */
    _isFollowMeRecorderChecked() {
        const { _followMeRecorderActive, _followMeRecorderEnabled } = this.props;
        return _followMeRecorderEnabled && !_followMeRecorderActive;
    }
    /**
     * Whether the recorder follow me option is disabled: another moderator
     * has one of the follow me modes active, or a recording is already in
     * progress — the recorder cannot pick up the change after it started.
     *
     * @returns {boolean}
     */
    _isFollowMeRecorderDisabled() {
        const { _followMeActive, _followMeRecorderActive, recordingRunning } = this.props;
        return _followMeActive || _followMeRecorderActive || recordingRunning;
    }
    /**
     * Sings in a user.
     *
     * @returns {void}
     */
    _onSignIn() {
        sendAnalytics(createRecordingDialogEvent('start', 'signIn.button'));
        this.props.dispatch(authorizeDropbox());
    }
    /**
     * Sings out an user from dropbox.
     *
     * @returns {void}
     */
    _onSignOut() {
        sendAnalytics(createRecordingDialogEvent('start', 'signOut.button'));
        this.props.dispatch(updateDropboxToken());
    }
}
/**
 * Maps part of the redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {IProps}
 */
export function mapStateToProps(state) {
    const { localRecording, recordingService } = state['features/base/config'];
    const _localRecordingAvailable = !localRecording?.disable && supportsLocalRecording();
    const canManageRecordingOrTranscription = isLocalParticipantModerator(state) || hasRecordingOrTranscriptionFeature(state);
    const { followMeRecorderEnabled } = state['features/follow-me'];
    const subtitlesLanguage = state['features/subtitles']._language?.replace('translation-languages:', '');
    return {
        ..._abstractMapStateToProps(state),
        isVpaas: isVpaasMeeting(state),
        _availableLanguages: getAvailableSubtitlesLanguages(state, subtitlesLanguage)
            .map((lang) => `translation-languages:${lang}`),
        _canManageRecordingOrTranscription: canManageRecordingOrTranscription,
        _canStartTranscribing: canAddTranscriber(state),
        _e2eeEnabled: isE2EEOnInMeeting(state),
        _followMeActive: isFollowMeActive(state),
        _followMeRecorderActive: isFollowMeRecorderActive(state),
        _followMeRecorderEnabled: Boolean(followMeRecorderEnabled),
        _isLiveStreamRunning: isLiveStreamingRunning(state),
        _hideStorageWarning: Boolean(recordingService?.hideStorageWarning),
        _isModerator: isLocalParticipantModerator(state),
        _renderRecording: isJwtFeatureEnabled(state, MEET_FEATURES.RECORDING, false),
        _transcriptionRunning: canManageRecordingOrTranscription ? isRecorderTranscriptionsRunning(state) : false,
        _localRecordingAvailable,
        _localRecordingEnabled: !localRecording?.disable,
        _localRecordingRunning: Boolean(state['features/recording'].localRecordingRunning),
        _localRecordingSelfEnabled: !localRecording?.disableSelfRecording,
        _localRecordingNoNotification: !localRecording?.notifyAllParticipants,
        _styles: ColorSchemeRegistry.get(state, 'StartRecordingDialogContent')
    };
}
export default AbstractStartRecordingDialogContent;
